import {
  handleSetUrlSearchParams,
  SYSTEM_PROBLEM_PARAM,
  SystemProblemCategory,
  SystemProblemSeverity,
} from '@vgrubs/components';
import { useSearchParams } from 'react-router-dom';
import { SystemProblemView } from '../helpers/systemProblemLabels';

/**
 * What the System Problems page is showing lives in the URL, as on Support
 * Tickets: a link to "the Queens one" has to land on it, and the open problem
 * is a param rather than a route so the list underneath keeps its place.
 *
 * Every param but the open problem is prefixed with `problem`, from when this
 * was a Dashboard tab sharing the URL with the Orders and Deliveries tabs'
 * own `locationId`. Kept so links made back then still filter the same.
 */
export const useSystemProblemFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Absent means All statuses: the view everyone starts on writes no param.
  const view =
    (searchParams.get('problemStatus') as SystemProblemView) || 'all';
  /* Set by the counters, and by the Orders tab's urgent bar. */
  const severity =
    (searchParams.get('problemSeverity') as SystemProblemSeverity) || null;
  /* Set by the category chips. */
  const category =
    (searchParams.get('problemCategory') as SystemProblemCategory) || null;
  const locationParam = searchParams.get('problemLocationId');
  const locationId = locationParam ? Number(locationParam) : null;
  /* Beside the id so the picker can show a name on a cold load. */
  const locationName = searchParams.get('problemLocationName');

  const problemParam = searchParams.get(SYSTEM_PROBLEM_PARAM);
  const problemId = problemParam ? Number(problemParam) : null;

  const set = (key: string, value: string) =>
    handleSetUrlSearchParams(key, value, setSearchParams);

  return {
    view,
    severity,
    category,
    locationId,
    locationName,
    problemId,
    setView: (next: SystemProblemView) =>
      set('problemStatus', next === 'all' ? '' : next),
    /** Picking the one already picked clears it - the counters toggle. */
    setSeverity: (next: SystemProblemSeverity | null) =>
      set('problemSeverity', !next || next === severity ? '' : next),
    setCategory: (next: SystemProblemCategory | null) =>
      set('problemCategory', next ?? ''),
    setLocation: (id: number | null, name?: string | null) => {
      set('problemLocationId', id === null ? '' : String(id));
      set('problemLocationName', id === null ? '' : name || '');
    },
    /* Replaces the history entry instead of adding one, on open and on
       close: Back then leaves the page rather than stepping through every
       problem that was looked at. A link or a push to ?problem=<id> still
       opens it. */
    openProblem: (id: number | null) =>
      handleSetUrlSearchParams(
        SYSTEM_PROBLEM_PARAM,
        id === null ? '' : String(id),
        setSearchParams,
        true
      ),
  };
};
